/**
 * Katalog der persoenlichen Schutzausruestung (PSA) fuer die
 * Gefaehrdungsbeurteilung (SiFa-Modul GBU).
 *
 * ─── Wofuer ──────────────────────────────────────────────────────────────
 * Eine Massnahme der Art 'P' traegt `psaCode` aus diesem Katalog
 * (`sifa_gbu_massnahmen.psa_code`). Daraus entstehen PSA-Uebersicht je
 * Arbeitsbereich, Piktogramme im PDF und spaeter die Betriebsanweisung.
 * Freitext statt Code liesse sich nicht zaehlen und nicht bebildern.
 *
 * ─── Repo statt Datenbank, Codes unveraenderlich ─────────────────────────
 * Begruendung wie in `gefaehrdungsfaktoren.ts`: die eingefrorene Fassung
 * nennt `kataloge.psa`, die Codes stehen in Massnahmen und Fassungen. Ein
 * Code wird nie umbenannt und nie neu vergeben.
 *
 * ─── Normen und Sicherheitszeichen ───────────────────────────────────────
 * `norm` steht nur, wo die Produktnorm sicher feststeht; sonst null.
 * `iso7010` ist das Gebotszeichen (M-Code) nach DIN EN ISO 7010 — nur wo
 * eindeutig, sonst null. Die Codes sind dieselben wie in `PIKTOGRAMME`
 * (`sige-plan/types.ts`) und in `asplan/kataloge/schutzklassenVorlagen.ts`:
 * Helm M014, Fussschutz M008, Handschutz M009, Augenschutz M004,
 * Gehoerschutz M003, Warnweste M015, Auffanggurt M018, Atemschutz M017,
 * Schutzkleidung M010, Gesichtsschutz M013. Das Bild und sein Pfad kommen
 * NUR ueber die Bruecke `asplan/piktogramme.ts` (`aufloesePikto`,
 * im PDF `piktoPfadLokal`) — hier steht bewusst kein Import, damit der
 * Katalog ohne die SiGe-Plan-Typen geladen werden kann. Der Test prueft,
 * dass jeder Code dort ein Label hat.
 *
 * ─── Beweiswert ──────────────────────────────────────────────────────────
 * Fassung 1, vom Betreiber fachlich zu pruefen, bevor sie aktiv wird
 * (docs/sifa-gbu-plan.md §5).
 *
 * ─── Quellen ─────────────────────────────────────────────────────────────
 * PSA-BV · DGUV Regeln der Reihe 112 (Benutzung von PSA) · DIN EN ISO 7010 ·
 * die jeweils genannte Produktnorm.
 */

/** Fassung des Katalogs. Bei jeder inhaltlichen Aenderung erhoehen. */
export const PSA_KATALOG_FASSUNG = 1;

export interface PsaEintrag {
  code: string;
  label: string;
  /** Kurzbeschreibung: wofuer, worauf achten. */
  beschreibung: string;
  /** Produktnorm, nur wenn sicher; sonst null. */
  norm: string | null;
  /** ISO-7010-Gebotszeichen (z. B. 'M014'), nur wenn eindeutig; sonst null. */
  iso7010: string | null;
}

export const PSA_KATALOG: ReadonlyArray<PsaEintrag> = [
  // ── Kopf ────────────────────────────────────────────────────────────────
  {
    code: 'schutzhelm',
    label: 'Schutzhelm',
    beschreibung: 'Industrieschutzhelm gegen herabfallende Gegenstände und Anstoßen; bei Absturzgefahr mit Kinnriemen.',
    norm: 'EN 397',
    iso7010: 'M014',
  },
  // ── Fuss ────────────────────────────────────────────────────────────────
  {
    code: 'sicherheitsschuhe_s1',
    label: 'Sicherheitsschuhe S1',
    beschreibung: 'Zehenschutzkappe, geschlossener Fersenbereich, antistatisch, Energieaufnahme im Fersenbereich; für trockene Bereiche ohne Durchtrittgefahr.',
    norm: 'EN ISO 20345',
    iso7010: 'M008',
  },
  {
    code: 'sicherheitsschuhe_s2',
    label: 'Sicherheitsschuhe S2',
    beschreibung: 'Wie S1, zusätzlich gegen Wasserdurchtritt und Wasseraufnahme.',
    norm: 'EN ISO 20345',
    iso7010: 'M008',
  },
  {
    code: 'sicherheitsschuhe_s3',
    label: 'Sicherheitsschuhe S3',
    beschreibung: 'Wie S2, zusätzlich durchtrittsicher mit profilierter Laufsohle — Regelfall auf Baustellen.',
    norm: 'EN ISO 20345',
    iso7010: 'M008',
  },
  {
    code: 'sicherheitsstiefel_s5',
    label: 'Sicherheitsgummistiefel S5',
    beschreibung: 'Stiefel aus Gummi oder Polymer mit Zehenschutzkappe, wasserdicht und durchtrittsicher — für nasse Bereiche (Betonieren, Erdarbeiten).',
    norm: 'EN ISO 20345',
    iso7010: 'M008',
  },
  // ── Hand ────────────────────────────────────────────────────────────────
  {
    code: 'handschuhe_mechanisch',
    label: 'Schutzhandschuhe (mechanisch)',
    beschreibung: 'Gegen Abrieb, Stich und Weiterreißen beim Umgang mit Baustoffen, Schalung und Werkzeug.',
    norm: 'EN 388',
    iso7010: 'M009',
  },
  {
    code: 'handschuhe_chemikalien',
    label: 'Chemikalienschutzhandschuhe',
    beschreibung: 'Material und Durchbruchzeit nach Sicherheitsdatenblatt wählen; Tragen flüssigkeitsdichter Handschuhe über 2 h je Tag zählt als Feuchtarbeit (TRGS 401).',
    norm: 'EN ISO 374-1',
    iso7010: 'M009',
  },
  {
    code: 'handschuhe_schnittschutz',
    label: 'Schnittschutzhandschuhe',
    beschreibung: 'Schnittfestigkeit nach EN 388 (Leistungsstufe A–F), z. B. beim Umgang mit Blechen, Glas oder Dämmstoffmessern.',
    norm: 'EN 388',
    iso7010: 'M009',
  },
  {
    code: 'handschuhe_vibration',
    label: 'Antivibrations-Schutzhandschuhe',
    beschreibung: 'Dämpfen nur einen Teil der Hand-Arm-Vibration; ersetzen weder vibrationsarme Geräte noch die Begrenzung der Einsatzzeit.',
    norm: 'EN ISO 10819',
    iso7010: 'M009',
  },
  // ── Gehoer ──────────────────────────────────────────────────────────────
  {
    code: 'gehoerschutz',
    label: 'Gehörschutz',
    beschreibung: 'Auswahl nach Schalldämmung und Lärmpegel; über 80 dB(A) zur Verfügung stellen, ab 85 dB(A) Tragepflicht.',
    norm: 'EN 352',
    iso7010: 'M003',
  },
  {
    code: 'gehoerschutz_kapsel',
    label: 'Kapselgehörschützer',
    beschreibung: 'Für wechselnde, kurze Lärmeinsätze; auch als Helmkapsel.',
    norm: 'EN 352-1',
    iso7010: 'M003',
  },
  {
    code: 'gehoerschutz_stoepsel',
    label: 'Gehörschutzstöpsel',
    beschreibung: 'Einweg- oder Mehrwegstöpsel, auch Otoplastik; für längere Tragezeiten.',
    norm: 'EN 352-2',
    iso7010: 'M003',
  },
  // ── Augen und Gesicht ───────────────────────────────────────────────────
  {
    code: 'schutzbrille',
    label: 'Schutzbrille',
    beschreibung: 'Bügel- oder Korbbrille gegen Partikel, Staub und Spritzer.',
    norm: 'EN 166',
    iso7010: 'M004',
  },
  {
    code: 'gesichtsschutz',
    label: 'Gesichtsschutzschirm (Visier)',
    beschreibung: 'Gegen wegfliegende Teile und Spritzer, z. B. beim Trennschleifen oder beim Umgang mit ätzenden Flüssigkeiten.',
    norm: 'EN 166',
    iso7010: 'M013',
  },
  {
    code: 'schweisserschutz',
    label: 'Schweißerschutzschirm/-schild',
    beschreibung: 'Augen- und Gesichtsschutz beim Schweißen mit passender Filterstufe.',
    norm: 'EN 175',
    iso7010: null,
  },
  // ── Atemschutz ──────────────────────────────────────────────────────────
  {
    code: 'atemschutz_ffp2',
    label: 'Atemschutz FFP2',
    beschreibung: 'Partikelfiltrierende Halbmaske gegen Stäube und Aerosole; Tragezeitbegrenzung und Vorsorge beachten.',
    norm: 'EN 149',
    iso7010: 'M017',
  },
  {
    code: 'atemschutz_ffp3',
    label: 'Atemschutz FFP3',
    beschreibung: 'Partikelfiltrierende Halbmaske der höchsten Schutzstufe; Auswahl nach Stoff und Konzentration, Tragezeitbegrenzung und Vorsorge beachten.',
    norm: 'EN 149',
    iso7010: 'M017',
  },
  {
    code: 'atemschutz_halbmaske_filter',
    label: 'Halbmaske mit Filter',
    beschreibung: 'Filter nach Stoff wählen: Partikelfilter (EN 143) oder Gas- und Kombinationsfilter (EN 14387).',
    norm: 'EN 140',
    iso7010: 'M017',
  },
  // ── Absturz ─────────────────────────────────────────────────────────────
  {
    code: 'psa_absturz',
    label: 'PSA gegen Absturz',
    beschreibung: 'Auffanggurt mit Verbindungsmittel und Falldämpfer (EN 355) oder Höhensicherungsgerät (EN 360) an geeignetem Anschlagpunkt; Unterweisung mit Übung und Rettungskonzept.',
    norm: 'EN 361',
    iso7010: 'M018',
  },
  // ── Koerper ─────────────────────────────────────────────────────────────
  {
    code: 'warnkleidung',
    label: 'Warnkleidung',
    beschreibung: 'Warnweste oder Warnkleidung (Klasse nach Verkehrsbereich) im Bereich von Baustellen- und Straßenverkehr.',
    norm: 'EN ISO 20471',
    iso7010: 'M015',
  },
  {
    code: 'schutzanzug_typ5_6',
    label: 'Einweg-Schutzanzug Typ 5/6',
    beschreibung: 'Typ 5 gegen Partikel, Typ 6 gegen begrenzte Flüssigkeitsspritzer — z. B. bei staubintensiven Abbruch- und Sanierungsarbeiten.',
    norm: 'EN ISO 13982-1 / EN 13034',
    iso7010: 'M010',
  },
  {
    code: 'wetterschutz',
    label: 'Wetterschutzkleidung (Regen)',
    beschreibung: 'Regenschutzkleidung für Arbeiten im Freien.',
    norm: 'EN 343',
    iso7010: null,
  },
  {
    code: 'kaelteschutz',
    label: 'Kälteschutzkleidung',
    beschreibung: 'Kälteschutz für Arbeiten im Freien in der kalten Jahreszeit; Kleidung für kühle Umgebungen nach EN 14058.',
    norm: 'EN 342',
    iso7010: null,
  },
  {
    code: 'sonnenschutz',
    label: 'Sonnen- und UV-Schutz',
    beschreibung: 'Kopfbedeckung mit Nackenschutz, körperbedeckende Kleidung und UV-Schutzmittel bei Arbeiten im Freien.',
    norm: null,
    iso7010: null,
  },
  {
    code: 'knieschutz',
    label: 'Knieschutz',
    beschreibung: 'Knieschoner oder Hose mit Kniepolstertaschen bei Arbeiten im Knien (Fliesen-, Estrich-, Bewehrungsarbeiten).',
    norm: 'EN 14404',
    iso7010: null,
  },
];

const NACH_CODE = new Map<string, PsaEintrag>(PSA_KATALOG.map((p) => [p.code, p]));

/** Ist `code` ein PSA-Code des Katalogs? (Der KI-Leser verwirft alles andere.) */
export const istPsaCode = (code: string | null | undefined): boolean =>
  typeof code === 'string' && NACH_CODE.has(code);

/** Katalogeintrag zum Code, sonst null. */
export const psaEintrag = (code: string | null | undefined): PsaEintrag | null =>
  (typeof code === 'string' && NACH_CODE.get(code)) || null;

/**
 * Anzeigename zum Code. Ein unbekannter Code kommt unveraendert zurueck statt
 * leer — eine Massnahme mit fremdem Code soll auffallen, nicht verschwinden.
 */
export const psaLabel = (code: string | null | undefined): string =>
  psaEintrag(code)?.label ?? (code ?? '');
